import React, { useState, useEffect } from 'react';
import { I18n } from 'react-redux-i18n';
import get from 'lodash/get';
import map from 'lodash/map';
import join from 'lodash/join';

import BarGraph from 'components/Charts/BarGraph';
import Card from 'components/Card';
import CardHeader from 'components/CardHeader';
import EvolutionIcon from 'components/EvolutionIcon';
import ExportButton from 'components/Export';
import Select from 'components/Select';
import ChartInputsBloc from '../ChartInputsBloc';

import { formatParam } from 'utils/formatParam';
import PriceContainer from './container';

const PriceWineCard = ({
  isBranded,
  priceData,
  formData,
  client_marques,
  client_wines,
  campaignId
}) => {
  const [state, setState] = useState({
    competitorNames: [],
    wine: null,
    vintageStart: null
  });

  const { competitorNames, wine, vintageStart } = state;

  const setDefaultValues = () => {
    let firstWine = null;

    if (isBranded) {
      const wineOptions = getWineOptionsFromBrands();
      const filteredClientWines = [...priceData.client_wines].filter((item) =>
        wineOptions.includes(item.wine)
      );
      firstWine = get(filteredClientWines, '[0]');
      if (!firstWine) {
        firstWine = get(priceData, 'client_wines[0]');
      }
    } else {
      const firstWineForm = get(formData, 'wine[0]');
      firstWine = priceData.client_wines.find(
        (item) => item.wine === firstWineForm.label
      );
      if (!firstWine) {
        firstWine = get(priceData, 'client_wines[0]');
      }
    }
    const firstWineOption = {
      label: get(firstWine, 'wine', ''),
      value: get(firstWine, 'wine', '')
    };
    const firstVintageOption = {
      label: get(firstWine, 'vintage', ''),
      value: get(firstWine, 'vintage', '')
    };
    const rivals = getRivals(firstWineOption, firstVintageOption);
    const competitorNames = getCompetitorNames(rivals);

    setState({
      wine: firstWineOption,
      vintageStart: firstVintageOption,
      competitorNames: competitorNames
        .slice(0, 5)
        .map((item) => ({ label: item, value: item }))
    });
  };

  useEffect(() => {
    if (!formData || !priceData || !client_wines) return;
    setDefaultValues();
  }, [client_wines, priceData, formData]);

  const handleChangeChart = (field, value) => {
    if (field === 'wine') {
      let rivals = getRivals(value);
      let competitorNames = getCompetitorNames(rivals)
        .slice(0, 5)
        .map((item) => ({ label: item, value: item }));
      setState({
        ...state,
        wine: value,
        competitorNames: competitorNames,
        vintageStart: null
      });
    } else {
      let rivals = getRivals(state.wine, value);
      let competitorNames = getCompetitorNames(rivals)
        .slice(0, 5)
        .map((item) => ({ label: item, value: item }));
      setState({ ...state, [field]: value, competitorNames });
    }
  };

  const filterDataSet = (data, wine) => {
    return [...data].filter(
      (item) =>
        item.format === '750' &&
        item.median_price &&
        vintageStart &&
        item.vintage == vintageStart.value &&
        wine &&
        item.wine === wine.label
    );
  };

  const getVintageOptions = (wineOptions) => {
    const data = priceData?.client_wines || [];
    return [
      ...new Set(
        data
          .filter((item) => wineOptions.includes(item.wine))
          .map((item) => item.vintage)
      )
    ]
      .sort()
      .reverse()
      .map((item) => ({ label: item, value: item }));
  };

  const formatVintageChart = () => {
    const { client_wines = [], rival_wines = [] } = priceData || {};
    let formattedData = [...client_wines];
    formattedData = filterDataSet(formattedData, wine);
    formattedData = [formattedData];
    if (competitorNames.length > 0) {
      competitorNames.forEach((tag) => {
        const competitorData = filterDataSet(rival_wines, tag);
        formattedData.push(competitorData);
      });
    }
    return formattedData.map((set) =>
      set
        .map((item) => ({
          ...item,
          name: item.vintage,
          minPrice: { icon: 'down', value: item.min_price },
          value: { icon: 'neutral', value: item.median_price },
          maxPrice: { icon: 'up', value: item.max_price }
        }))
        .sort(function (a, b) {
          return String(a.vintage).localeCompare(String(b.vintage));
        })
    );
  };

  const getRivalsList = (wine) => {
    const clientWines = client_wines || [];
    return get(
      clientWines.find((item) => wine && item.wine_name === wine.label),
      'rivals',
      []
    );
  };

  const getRivals = (wineParam, vintageStartParam) => {
    const rival_wines = priceData?.rival_wines || [];
    const client_wines = priceData?.client_wines || [];
    const wineValue = wineParam || wine;
    const vintageValue = vintageStartParam || vintageStart;

    if (!client_wines.length) {
      return [];
    }

    const rivalList = getRivalsList(wineValue);

    return rival_wines.filter(
      (item) =>
        wineValue &&
        item.format === '750' &&
        item.median_price &&
        (!isNaN(parseInt(item.vintage, 10)) ||
          item.vintage === 'nv' ||
          item.vintage === 'NV') &&
        vintageValue &&
        rivalList.includes(item.wine) &&
        item.vintage == vintageValue.value
    );
  };

  const getCompetitorNames = (data) => {
    return [...new Set(data.map((item) => item.wine))];
  };

  const renderSelectSets = () => {
    let rivals = getRivals();
    let rivalOptions = getCompetitorNames(rivals);
    return (
      <div className="selectSetsContainer ignored-export">
        <div className="selecSetstInputContainer">
          <Select
            placeholder={I18n.t('placeholderRivals')}
            minWidth="300px"
            isMulti
            value={competitorNames}
            dropdownProps={{
              styles: `margin-left:20px;margin-right:20px;`
            }}
            customStyles={{
              valueContainer: {
                overflowY: 'auto',
                maxHeight: '45px'
              }
            }}
            onChange={(value) =>
              setState({
                ...state,
                competitorNames:
                  value && value.length > 5
                    ? value.slice(1, value.length)
                    : value
              })
            }
            options={rivalOptions.map((key) => ({
              value: key,
              label: key
            }))}
          />
        </div>
      </div>
    );
  };

  const renderTooltip = (data, labels) => {
    return (
      <div className="rhcTooltip">
        <div className="rhcTooltipContainer">
          <div className="rhcTooltipValues">
            <p className="rhcLabel">{I18n.t('lowPrice')}</p>
            <p className="rhcLabel">{I18n.t('medianPrice')}</p>
            <p className="rhcLabel">{I18n.t('highPrice')}</p>
          </div>
        </div>
        {data.map(
          (set, index) =>
            set &&
            labels[index] && (
              <React.Fragment key={index}>
                <div className="rhcTooltipContainer">
                  <p>{`${labels[index]} ${set.name}`}</p>
                  <div className="rhcTooltipValues">
                    {Object.keys(set).map(
                      (key) =>
                        set[key] &&
                        set[key].value && (
                          <div key={key}>
                            <EvolutionIcon icon={set[key].icon} />
                            <p>
                              {set[key].value.toLocaleString('fr-FR', {
                                style: 'currency',
                                currency: set.displayed_currency
                                  ? set.displayed_currency
                                  : 'EUR',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2
                              })}
                            </p>
                          </div>
                        )
                    )}
                  </div>
                </div>
              </React.Fragment>
            )
        )}
      </div>
    );
  };

  const getLabels = () => {
    if (wine) {
      return [wine.label].concat(competitorNames.map((item) => item.value));
    }
    return [];
  };

  const getWineOptionsFromBrands = () => {
    const { client_wines } = priceData || {};
    if (!client_wines || !client_wines.length === 0) {
      return [];
    }
    const { wine } = formData;
    const winelabels = wine.map((item) => item.label);
    const wineOptions = [];
    client_marques.forEach((marque) => {
      if (winelabels.includes(marque.marque_name)) {
        wineOptions.push(...marque.wines);
      }
    });
    return client_wines
      .filter((item) => wineOptions.includes(item.wine))
      .map((item) => item.wine);
  };

  const getWineOptions = () => {
    const { client_wines = [] } = priceData || {};
    if (isBranded) {
      return [...new Set(getWineOptionsFromBrands())]
        .map((item) => ({
          label: item,
          value: item
        }))
        .sort((a, b) => String(a.label).localeCompare(String(b.label)));
    }
    return [...new Set(client_wines.map((item) => item.wine))]
      .sort()
      .map((item) => ({ label: item, value: item }));
  };

  const dataVintageChart = formatVintageChart();
  const labels = getLabels(dataVintageChart);
  const wineOptions = getWineOptions();

  const { competitors } = formData;

  const exportPath = `/distribution/${campaignId}/price/xls?`;

  const handleExportOptions = isBranded
    ? {
        brands: formatParam(
          JSON.stringify(map(get(formData, 'wine'), 'label'))
        ),
        rivals: formatParam(JSON.stringify(competitors))
      }
    : {
        wines: formatParam(JSON.stringify(map(get(formData, 'wine'), 'label'))),
        rivals: formatParam(JSON.stringify(competitors))
      };

  return (
    <PriceContainer>
      <Card id="prix-moyen-comparaison-concurrentielle">
        <CardHeader
          alignItems="center"
          title={I18n.t('restaurants&caves.price.byRivalsTitle')}
          styles={`.cardHeaderRight{width:100%;margin-top:10px;};flex-direction:column;align-items:flex-start;`}
          fontSize="20px"
          iconRight="right"
          rightComponents={
            <div className="rightComponentsContainer ignored-export">
              <div className="rightComponents">
                <ChartInputsBloc
                  wine={wine}
                  noVintageEnd
                  vintageStart={vintageStart}
                  wineOptions={wineOptions}
                  bigOptions={isBranded}
                  vintageOptions={getVintageOptions(
                    wineOptions.map((item) => item.label)
                  )}
                  onChange={(field, value) => handleChangeChart(field, value)}
                />

                <ExportButton
                  key={`export-${campaignId}`}
                  name={`${I18n.t(
                    'restaurants&caves.price.byRivalsTitle'
                  )}-${get(formData, 'currentCampaign.market.name')}-${join(
                    map(get(formData, 'wine'), 'label'),
                    ''
                  )}`}
                  targetElement="#prix-moyen-comparaison-concurrentielle"
                  jpg
                  pdf
                  xls
                  path={exportPath}
                  options={handleExportOptions}
                  newApi
                />
              </div>
            </div>
          }
        />
        <BarGraph
          height={380}
          renderTooltip={(props) => renderTooltip(props, labels)}
          data={dataVintageChart}
          tooltipOffset={{ x: 5, y: 30 }}
          yAxisProps={{
            tickFormatter: (value) =>
              value.toLocaleString('fr-FR', {
                style: 'currency',
                currency: get(
                  dataVintageChart,
                  '[0][0].displayed_currency',
                  'EUR'
                ),
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }),
            domain: [0, (dataMax) => dataMax + dataMax / 2]
          }}
          labels={labels}
          colors={[
            '#e25d34',
            '#1f2b37',
            '#4c555f',
            '#70787f',
            '#949a9f',
            '#b7bbbf',
            '#dbdddf',
            '#DAE3EB'
          ]}
        />
        {renderSelectSets()}
      </Card>
    </PriceContainer>
  );
};

export default PriceWineCard;
